import { Observable } from 'rxjs';
import { Injectable, TemplateRef } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';

import {
  SgToasterComponent,
  SgToasterComponentContext,
  SgToasterComponentOptions,
  SgToasterInternalRef,
  SgToasterComponentType,
  SgToasterItemTemplateContext,
} from './toaster.component';
import { SgAsyncGuiContent } from '@sg/ui-kit/async-gui';

export class SgToasterRef<T = any> {
  get item(): SgToasterComponentContext {
    return this.ref.item;
  }
  get closed(): boolean {
    return this.ref.closed;
  }

  constructor(private readonly ref: SgToasterInternalRef) {}

  convertTo(type: SgToasterComponentType, content: SgAsyncGuiContent, options: SgToasterComponentOptions = {}): void {
    this.ref.item.type = type;
    this.ref.item.content = content;
    this.ref.update(options);
  }

  afterOpened(): Observable<void> {
    return this.ref.afterOpened();
  }

  afterDismissed(): Observable<void> {
    return this.ref.afterDismissed();
  }

  close(): boolean {
    if (!this.closed) {
      return this.ref.close();
    }
    return true;
  }
}

// Old service, use SgToastService from @sg/ui-kit/core instead
@Injectable()
export class SgToasterService {
  toasterTemplateRef: TemplateRef<SgToasterItemTemplateContext>;

  private current: MatSnackBarRef<SgToasterComponent>;

  constructor(private snackBar: MatSnackBar) {}

  open<T = any>(data: SgToasterComponentContext): SgToasterRef<T> {
    if (!this.current) {
      this.current = this.snackBar.openFromComponent(SgToasterComponent, {
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['sg-toaster-container-panel', data.extraClass],
        data: {
          toasterTemplateRef: this.toasterTemplateRef,
        },
        duration: 0,
      });
      this.current.afterDismissed().subscribe(() => (this.current = undefined));
    }
    return new SgToasterRef<T>(this.current.instance.addToaster(data));
  }

  success<T = any>(content: SgAsyncGuiContent, options: SgToasterComponentOptions = {}): SgToasterRef<T> {
    return this.open<T>({ ...options, type: 'success', content });
  }

  wait<T = any>(content: SgAsyncGuiContent, options: SgToasterComponentOptions = {}): SgToasterRef<T> {
    return this.open<T>({ ...options, type: 'wait', content });
  }

  info<T = any>(content: SgAsyncGuiContent, options: SgToasterComponentOptions = {}): SgToasterRef<T> {
    return this.open<T>({ ...options, type: 'info', content });
  }

  warn<T = any>(content: SgAsyncGuiContent, options: SgToasterComponentOptions = {}): SgToasterRef<T> {
    return this.open<T>({ ...options, type: 'warn', content });
  }

  error<T = any>(content: SgAsyncGuiContent, options: SgToasterComponentOptions = {}): SgToasterRef<T> {
    return this.open<T>({ ...options, type: 'error', content });
  }
}
